import { doc, getDoc } from "firebase/firestore";
import { projectFirestore } from "../firebase";
import { piecesOnRow } from "../pages/items/batchPieceCount";
import { lineHoldReason } from "./unbatchSettlement";

export const UNBATCHES_KEY = "riverr.unbatches";
export const UNBATCH_STARTED = "riverr:unbatch-started";
export const UNBATCH_FINISHED = "riverr:unbatch-finished";
const FORGET_AFTER_MS = 60 * 60 * 1000;

export const readUnbatches = () => {
  try {
    const rows = JSON.parse(localStorage.getItem(UNBATCHES_KEY) || "[]");
    if (!Array.isArray(rows)) return [];
    const cutoff = Date.now() - FORGET_AFTER_MS;
    return rows.filter((row) => row?.batchId && row.startedAt > cutoff);
  } catch (_) {
    return [];
  }
};

export const writeUnbatches = (rows) => {
  try {
    localStorage.setItem(UNBATCHES_KEY, JSON.stringify(rows));
  } catch (_) {
    /* ignore */
  }
};

const cleanIds = (ids) =>
  Array.from(new Set((ids || []).filter(Boolean).map(String)));

/**
 * Read what a batch holds before unbatching deletes it, so the tray knows
 * which order lines should come back to the waiting list.
 */
export const readBatchContents = async (enterpriseId, batch = {}) => {
  let data = batch || {};
  if (enterpriseId && batch?.id) {
    try {
      const snap = await getDoc(
        doc(projectFirestore, "enterprises", enterpriseId, "batches", String(batch.id))
      );
      if (snap.exists()) data = { ...batch, ...snap.data() };
    } catch (_) {
      /* fall back to what the list already has */
    }
  }
  return {
    orderIds: cleanIds(data.orderIds),
    orderItemIds: cleanIds(data.orderItemIds),
    itemCount:
      Number(data.itemsCount) ||
      (Array.isArray(data.itemIds) ? data.itemIds.length : 0) ||
      0,
  };
};

// The unbatch request itself can run for minutes on a large batch. If the tab
// closed mid-request we never hear back, so stop waiting on it after this.
const REQUEST_GIVE_UP_MS = 10 * 60 * 1000;

export const isUnbatchRequesting = (job) =>
  Boolean(job?.requesting) && Date.now() - job.startedAt < REQUEST_GIVE_UP_MS;

/**
 * Call when the user confirms unbatch, before the request returns. Pass
 * `requesting: true` and call finishUnbatchRequest when it settles; the tray
 * then follows items back to the queue.
 */
export const trackUnbatch = ({
  batchId,
  batchNumber = "",
  enterpriseId,
  orderIds,
  orderItemIds,
  itemCount = 0,
  requesting = false,
}) => {
  const orders = cleanIds(orderIds);
  if (!batchId || !orders.length) return false;
  const rows = readUnbatches().filter((row) => row.batchId !== String(batchId));
  rows.push({
    batchId: String(batchId),
    batchNumber: batchNumber ? String(batchNumber) : "",
    enterpriseId: enterpriseId ? String(enterpriseId) : "",
    orderIds: orders,
    orderItemIds: cleanIds(orderItemIds),
    itemCount: Number(itemCount) || 0,
    startedAt: Date.now(),
    requesting,
  });
  writeUnbatches(rows);
  window.dispatchEvent(new CustomEvent(UNBATCH_STARTED));
  return true;
};

/** ok=false drops the card: the batch is still intact. */
export const finishUnbatchRequest = (batchId, ok) => {
  const id = String(batchId);
  const rows = readUnbatches()
    .filter((row) => ok || row.batchId !== id)
    .map((row) => (row.batchId === id ? { ...row, requesting: false } : row));
  writeUnbatches(rows);
  window.dispatchEvent(new CustomEvent(UNBATCH_STARTED));
};

/**
 * Unbatch deletes the batch doc before sending lines back. If this tab
 * reloaded mid-request it never hears the response, so the doc going away is
 * the other signal that the request went through.
 */
export const isBatchGone = async (job) => {
  if (!job?.enterpriseId || !job?.batchId) return false;
  const snap = await getDoc(
    doc(projectFirestore, "enterprises", job.enterpriseId, "batches", String(job.batchId))
  );
  return !snap.exists();
};

/** Close the confirm dialog right away; the tray shows progress and errors. */
export const startUnbatch = async ({ batch, enterpriseId, request, onError }) => {
  const existing = readUnbatches().find(
    (row) => row.batchId === String(batch?.id)
  );
  if (isUnbatchRequesting(existing)) return;
  const contents = await readBatchContents(enterpriseId, batch);
  const tracked = trackUnbatch({
    batchId: batch?.id,
    batchNumber: batch?.batchNumber || "",
    enterpriseId,
    ...contents,
    requesting: true,
  });
  let res;
  try {
    res = await request();
  } catch (error) {
    res = { error };
  }
  if (tracked) finishUnbatchRequest(batch?.id, !res?.error);
  if (res?.error) onError?.(res.error);
  return res;
};

/** Pieces already back on the waiting list, counting each order line once. */
export const returnedPieceCount = (rows, orderItemIds) => {
  const wanted = new Set((orderItemIds || []).map(String));
  const seen = new Set();
  let back = 0;
  for (const row of rows || []) {
    const lineId = String(row?.orderItemId || "");
    if (wanted.size && !wanted.has(lineId)) continue;
    const key = lineId || String(row?.id || "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    back += piecesOnRow(row);
  }
  return back;
};

let progressQuery = null;

/**
 * How many pieces are back on the waiting list. Uses the batch's piece total
 * when it was recorded; otherwise counts order lines, then orders.
 */
export const countReturned = async (job) => {
  const [{ gql }, { default: client }] = await Promise.all([
    import("@apollo/client"),
    import("../graphQL"),
  ]);
  if (!progressQuery) {
    progressQuery = gql`
      query UnbatchProgress($filters: [FilterInput!]) {
        getItems(filters: $filters) {
          items {
            id
            orderId
            orderItemId
            quantity
          }
        }
      }
    `;
  }
  const res = await client.query({
    query: progressQuery,
    variables: { filters: [{ id: "Orders", values: job.orderIds }] },
    fetchPolicy: "network-only",
  });
  const rows = res?.data?.getItems?.items || [];
  if (job.itemCount) {
    return {
      back: returnedPieceCount(rows, job.orderItemIds),
      total: Number(job.itemCount),
      unit: "items",
    };
  }
  if (job.orderItemIds?.length) {
    const wanted = new Set(job.orderItemIds);
    const back = new Set(
      rows.map((row) => String(row?.orderItemId)).filter((id) => wanted.has(id))
    );
    return { back: back.size, total: wanted.size, unit: "items" };
  }
  const wanted = new Set(job.orderIds);
  const back = new Set(
    rows.map((row) => String(row?.orderId)).filter((id) => wanted.has(id))
  );
  return { back: back.size, total: wanted.size, unit: "orders" };
};

const readDocs = async (collectionName, ids) => {
  const unique = cleanIds(ids);
  const snaps = await Promise.all(
    unique.map((id) => getDoc(doc(projectFirestore, collectionName, id)))
  );
  return new Map(unique.map((id, index) => [id, snaps[index]]));
};

/**
 * One read of the lines on this unbatch. "pending" means the line is still
 * marked on this batch, or the order is back in pre-production and the queue
 * row may still appear. Any other reason will not show up on the waiting list.
 */
export const linesHeldFromQueue = async (job) => {
  const lineIds = cleanIds(job?.orderItemIds);
  const lineDocs = await readDocs("orderItems", lineIds);
  const orderIds = cleanIds([
    ...(job?.orderIds || []),
    ...Array.from(lineDocs.values()).map((snap) => snap.data()?.orderId),
  ]);
  const orderDocs = await readDocs("orders", orderIds);
  const sellerIds = cleanIds(
    Array.from(orderDocs.values()).map((snap) => snap.data()?.uid)
  );
  const sellerDocs = await readDocs("users", sellerIds);
  const sellerOnHold = (uid) =>
    Boolean(uid && sellerDocs.get(String(uid))?.data()?.ordersOnHold === true);

  if (lineIds.length) {
    return lineIds.map((id) => {
      const snap = lineDocs.get(id);
      const orderItem = snap?.exists() ? snap.data() : null;
      const order = orderItem?.orderId
        ? orderDocs.get(String(orderItem.orderId))?.data() || null
        : null;
      return {
        reason: lineHoldReason({
          orderItem,
          order,
          batchId: job.batchId,
          sellerOnHold: sellerOnHold(order?.uid),
        }),
      };
    });
  }

  return orderIds.map((id) => {
    const order = orderDocs.get(id)?.data() || null;
    return {
      reason: lineHoldReason({
        orderItem: null,
        order,
        batchId: job.batchId,
        sellerOnHold: sellerOnHold(order?.uid),
      }),
    };
  });
};
